import { useMutation } from '@apollo/client';
import { Button } from 'components/Button/Button';
import { Input } from 'components/Input/Input';
import { Label } from 'components/Label/Label';
import { Modal } from 'components/Modal/Modal';
import { Portal } from 'components/Portal/Portal';
import { useToast } from 'components/Toast/use-toast';
import dayjs from 'dayjs';
import { extractCaughtError, extractGraphQLError } from 'lib/graphql-error-handler';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EXPORT_BALANCES_AS_OF } from 'services/consumption/mutation';
import { downloadCsv } from 'utils/downloadCsv';

const MIN_DATE = '2025-11-03';

type ExportBalancesAsOfData = {
    exportBalancesAsOf: {
        content: string | null;
        filename: string | null;
        count: number | null;
    } | null;
};

export const ExportBalancesAsOf = () => {
    const { t } = useTranslation('documents');
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [asOf, setAsOf] = useState('');
    const [until, setUntil] = useState('');
    const [exportBalances, { loading }] = useMutation<ExportBalancesAsOfData>(EXPORT_BALANCES_AS_OF);
    const today = dayjs().format('YYYY-MM-DD');

    const showError = (message: string) =>
        toast({ title: t('balancesExportErrorTitle'), description: message, variant: 'warning' });

    const closeModal = () => {
        if (loading) return;
        setIsOpen(false);
    };

    const doExport = async () => {
        try {
            const resp = await exportBalances({ variables: { asOf, until: until || null } });
            if (resp.errors) {
                showError(extractGraphQLError(resp.errors, t('balancesExportErrorMessage')));
                return;
            }
            const result = resp.data?.exportBalancesAsOf;
            if (!result?.content) {
                showError(t('balancesExportErrorMessage'));
                return;
            }
            downloadCsv(result.content, result.filename ?? 'saldo.csv');
            setIsOpen(false);
        } catch (e) {
            showError(extractCaughtError(e, t('balancesExportErrorMessage')));
        }
    };

    return (
        <>
            <Button variant="outline" onClick={() => setIsOpen(true)}>
                {t('balancesAsOfButton')}
            </Button>
            <Portal>
                <Modal isOpen={isOpen} onClose={closeModal} maxWidth={512}>
                    <div className="flex flex-col gap-4">
                        <div className="text-xl font-semibold text-zinc-700">{t('balancesAsOfButton')}</div>
                        <div>
                            <Label htmlFor="balances-as-of-from">{t('balancesAsOfFrom')}</Label>
                            <Input
                                id="balances-as-of-from"
                                type="date"
                                min={MIN_DATE}
                                max={today}
                                value={asOf}
                                onChange={e => setAsOf(e.target.value)}
                            />
                        </div>
                        <div>
                            <Label htmlFor="balances-as-of-until">{t('balancesAsOfUntil')}</Label>
                            <Input
                                id="balances-as-of-until"
                                type="date"
                                min={asOf || MIN_DATE}
                                max={today}
                                value={until}
                                onChange={e => setUntil(e.target.value)}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={closeModal} disabled={loading}>
                                {t('balancesExportCancel')}
                            </Button>
                            <Button onClick={doExport} disabled={!asOf || loading}>
                                {t('balancesExportSubmit')}
                            </Button>
                        </div>
                    </div>
                </Modal>
            </Portal>
        </>
    );
};
